/**
 * A lesson text's sections, as the reader opens them one at a time.
 *
 * The text is written one plan section at a time: an intro (the plan's
 * summary), then each section under `## <its title>`. The backend lists the
 * plan's sections (`LessonText.sections`, from
 * `backend/src/lesson/text_sections.py`); this cuts the markdown along the
 * same headings (never inside a code block) and pairs the two.
 */
import type { TextSection } from '@/types/api';

const FENCE = /^\s*(```|~~~)/;
const HEADING = /^##\s+(.+?)\s*#*\s*$/;

export interface TextPart {
  title: string;
  body: string;
}

/** The intro before the first section, and each section's title and body. */
export function splitSections(markdown: string): {
  intro: string;
  sections: TextPart[];
} {
  const intro: string[] = [];
  const sections: { title: string; lines: string[] }[] = [];
  let inCode = false;
  for (const line of markdown.split('\n')) {
    if (FENCE.test(line)) inCode = !inCode;
    const m = inCode ? null : HEADING.exec(line);
    if (m) {
      sections.push({ title: m[1].trim(), lines: [] });
      continue;
    }
    (sections.length ? sections[sections.length - 1].lines : intro).push(line);
  }
  return {
    intro: intro.join('\n').trim(),
    sections: sections.map(s => ({
      title: s.title,
      body: s.lines.join('\n').trim(),
    })),
  };
}

const norm = (title: string) =>
  title
    .toLowerCase()
    .replace(/[^\p{L}\p{N}\s]/gu, ' ')
    .replace(/\s+/g, ' ')
    .trim();

/** A plan section with its part of the text (null until it is written). */
export interface OpenableSection extends TextSection {
  body: string | null;
}

/** The plan's sections, each with its body from `parts`: matched by title,
 *  else by position (a heading the writer reworded). */
export function pairSections(
  planned: TextSection[],
  parts: TextPart[]
): OpenableSection[] {
  const byTitle = new Map(parts.map(p => [norm(p.title), p]));
  return planned.map((s, i) => {
    const part =
      byTitle.get(norm(s.title)) ?? (s.ready ? parts[i] : undefined) ?? null;
    return {
      ...s,
      ready: s.ready && part !== null,
      body: part ? part.body : null,
    };
  });
}
